/**
 * Khảo sát hài lòng (TT 83 C9 — chỉ số 5.4). Mirror BE `apps/surveys/views.py` +
 * `admin_views.py`.
 */
import { apiClient } from "@/api/client";

export type QuestionKind = "likert5" | "text" | "single_choice";
export type CampaignTarget = "course_instructor" | "graduates";

export const LIKERT_LABELS: Record<number, string> = {
  1: "Rất không hài lòng",
  2: "Không hài lòng",
  3: "Bình thường",
  4: "Hài lòng",
  5: "Rất hài lòng",
};
export const TARGET_LABEL: Record<CampaignTarget, string> = {
  course_instructor: "Người học đánh giá giảng viên",
  graduates: "Người tốt nghiệp — hài lòng tổng thể",
};
export const INVALID_LABEL: Record<string, string> = {
  blank: "Bỏ trống",
  incomplete: "Thiếu câu bắt buộc",
  straight_lining: "Chọn cùng 1 mức cho mọi câu",
};

export interface SurveyQuestion {
  id: number;
  order: number;
  text: string;
  kind: QuestionKind;
  choices: string[];
  required: boolean;
  counts_toward_satisfaction: boolean;
}

export interface SurveyInvitation {
  id: number;
  campaign_name: string;
  target: CampaignTarget;
  closes_at: string;
  course_code: string | null;
  course_name: string | null;
  instructor_name: string | null;
}

export interface SurveyForm extends SurveyInvitation {
  title: string;
  is_official_moet: boolean;
  questions: SurveyQuestion[];
}

export interface AnswerInput {
  question: number;
  value_int?: number;
  value_text?: string;
}

export interface SurveyTemplate {
  id: number;
  code: string;
  version: number;
  title: string;
  is_official_moet: boolean;
  source_note: string;
  locked: boolean;
  questions: SurveyQuestion[];
  created_at: string;
}

export interface SurveyCampaign {
  id: number;
  name: string;
  target: CampaignTarget;
  template: number;
  template_code: string;
  template_version: number;
  term_offering: number | null;
  term_offering_name: string | null;
  course: number | null;
  graduated_from: string | null;
  graduated_to: string | null;
  opens_at: string;
  closes_at: string;
  status: "open" | "closed";
  invitations: number | null;
  responded: number | null;
  created_at: string;
}

export interface CampaignGroup {
  instructor_id: number;
  instructor_email: string;
  instructor_name: string;
  course_id: number | null;
  course_code: string | null;
  invitations: number;
  valid_responses: number;
  response_rate_pct: string | null;
  satisfaction_pct: string | null;
}

export interface CampaignReport {
  invitations: number;
  submitted: number;
  valid_responses: number;
  invalid: Record<string, number>;
  response_rate_pct: string | null;
  satisfaction_pct: string | null;
  satisfied_answers: number;
  counted_answers: number;
  groups: CampaignGroup[];
  comments: string[];
}

export interface MyResult {
  campaign_id: number;
  campaign_name: string;
  closes_at: string;
  course_code: string | null;
  invitations: number;
  valid_responses: number;
  hidden: boolean;
  satisfaction_pct: string | null;
}

// ─── Người học ──────────────────────────────────────────────────────────────

export async function listMySurveys(): Promise<{ results: SurveyInvitation[] }> {
  return (await apiClient.get("/surveys/my/")).data;
}
export async function getInvitationForm(id: number): Promise<SurveyForm> {
  return (await apiClient.get(`/surveys/invitations/${id}/`)).data;
}
export async function submitInvitation(id: number, answers: AnswerInput[]) {
  return (await apiClient.post(`/surveys/invitations/${id}/submit/`, { answers })).data;
}
export async function getTokenForm(token: string): Promise<SurveyForm> {
  return (await apiClient.get(`/surveys/t/${encodeURIComponent(token)}/`)).data;
}
export async function submitToken(token: string, answers: AnswerInput[]) {
  return (await apiClient.post(`/surveys/t/${encodeURIComponent(token)}/submit/`, { answers }))
    .data;
}

// ─── Quản trị ───────────────────────────────────────────────────────────────

const A = "/admin/surveys/";

export async function listTemplates(): Promise<{ results: SurveyTemplate[] }> {
  return (await apiClient.get(`${A}templates/`)).data;
}
export async function createTemplate(body: {
  code: string;
  title: string;
  is_official_moet: boolean;
  source_note: string;
  questions: Partial<SurveyQuestion>[];
}): Promise<SurveyTemplate> {
  return (await apiClient.post(`${A}templates/`, body)).data;
}
export async function listCampaigns(): Promise<{ results: SurveyCampaign[] }> {
  return (await apiClient.get(`${A}campaigns/`)).data;
}
export async function createCampaign(body: {
  template: number;
  name: string;
  target: CampaignTarget;
  term_offering?: number | null;
  graduated_from?: string | null;
  graduated_to?: string | null;
  opens_at: string;
  closes_at: string;
}): Promise<SurveyCampaign & { skipped_no_instructor: number }> {
  return (await apiClient.post(`${A}campaigns/`, body)).data;
}
export async function getCampaign(
  id: number,
): Promise<SurveyCampaign & { report: CampaignReport }> {
  return (await apiClient.get(`${A}campaigns/${id}/`)).data;
}
export async function sendCampaign(id: number, reminder: boolean) {
  return (await apiClient.post(`${A}campaigns/${id}/send/`, { reminder })).data;
}
export async function regenerateCampaign(id: number) {
  return (await apiClient.post(`${A}campaigns/${id}/regenerate/`)).data;
}
export async function closeCampaign(id: number): Promise<SurveyCampaign> {
  return (await apiClient.post(`${A}campaigns/${id}/close/`)).data;
}
export async function listMyResults(): Promise<{ min_responses: number; results: MyResult[] }> {
  return (await apiClient.get(`${A}my-results/`)).data;
}

/** Thiếu câu bắt buộc → danh sách số thứ tự câu (FE chặn trước khi gửi). */
export function missingRequired(questions: SurveyQuestion[], answers: AnswerInput[]): number[] {
  const done = new Set(
    answers
      .filter((a) => a.value_int !== undefined || (a.value_text ?? "").trim() !== "")
      .map((a) => a.question),
  );
  return questions.filter((q) => q.required && !done.has(q.id)).map((q) => q.order);
}
